// projectEditors — the controlled section editors shared by the create wizard AND
// the edit drawers (so the two surfaces can't drift): the country multi-select, the
// tier/seats editor, the add-on toggles — and the vocabularies the two pick from
// (the offerable countries · the default country among the chosen · the trial
// lengths). Each editor is a plain controlled component (value + onChange); the
// hosting form binds them via RHF watch/setValue.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LicenseTier } from '@ems-app/schemas/projects/platform-responses';
import { Picker } from '../../../components/patterns/Picker.js';
import { Checkbox } from '../../../components/ui/Checkbox.js';
import { NumberInput } from '../../../components/ui/NumberInput.js';
import { Toggle } from '../../../components/ui/Toggle.js';
import { Alert } from '../../../components/ui/Alert.js';
import { useOfferableCountries } from '../../../hooks/usePlatformProjects.js';
import { codeSource, parentedCodeSource, type CodeSource, type ParentedCodeSource } from '../../../hooks/useOptions.js';
import { countryName, formatNumber } from '../../../lib/format.js';
import { TRIAL_LENGTHS, type TierDraft } from './projectForm.js';

type Modules = { has_invoicing: boolean; has_field_force: boolean; has_vehicles: boolean };

// ── the vocabularies ─────────────────────────────────────────────────────────

/** The offerable countries as a vocabulary (ADR-080 §3 — built from the page's one read, "Loading…" while it loads):
 *  named in the viewer's language, sorted by that name, found by name or ISO code (the code is the row's line). */
export function useCountrySource(): CodeSource {
  const { i18n } = useTranslation();
  const countries = useOfferableCountries();
  return useMemo(() => {
    const collator = new Intl.Collator(i18n.language);
    const rows = (countries.data ?? [])
      .map((c) => ({ id: c.iso_code, label: countryName(c.iso_code, i18n.language), description: c.iso_code, pickable: true, group: null }))
      .sort((a, b) => collator.compare(a.label, b.label));
    return { ...codeSource(`platform.countries:${i18n.language}:${rows.length}`, rows), pending: countries.isPending };
  }, [countries.data, countries.isPending, i18n.language]);
}

/** The default country among the CHOSEN ones — each row stands under itself, so the field declares the chosen set as
 *  its parent (`must_belong`): a country taken off the list clears the default and says it (ADR-080 §9). */
export function useDefaultCountrySource(chosen: readonly string[]): ParentedCodeSource {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const collator = new Intl.Collator(i18n.language);
    const rows = chosen
      .map((iso) => ({ id: iso, label: countryName(iso, i18n.language), description: iso, parent: iso, pickable: true, group: null }))
      .sort((a, b) => collator.compare(a.label, b.label));
    return parentedCodeSource(`platform.default-country:${i18n.language}:${[...chosen].sort().join(',')}`, rows);
  }, [chosen, i18n.language]);
}

/** The trial lengths, shortest first (the create wizard's and the trial drawer's "Extend by"). */
export function useTrialLengthSource(): CodeSource {
  const { t, i18n } = useTranslation('platform-projects');
  return useMemo(
    () => codeSource(`platform.trial-lengths:${i18n.language}`, TRIAL_LENGTHS.map((l) => ({ id: l, label: t(`trialLength.${l}`), pickable: true, group: null }))),
    [t, i18n.language],
  );
}

// ── country multi-select ────────────────────────────────────────────────────
// The family's picker over every offerable country; the chosen show as removable tags.
export function CountriesField({
  value,
  onChange,
  help,
  error,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  help?: string;
  error?: string;
}) {
  const { t } = useTranslation('platform-projects');
  const source = useCountrySource();
  return (
    <Picker
      source={source}
      multiple
      selectedDisplay="tags"
      selectedItemsLabel={t('countryPicker.selectedAria')}
      label={t('countryPicker.label')}
      placeholder={t('countryPicker.placeholder')}
      required
      value={value}
      onChange={(next) => onChange([...next])}
      help={help}
      error={error}
    />
  );
}

// ── tier / seats editor ───────────────────────────────────────────────────────
// Reads the GLOBAL tier catalogue (never assumes a fixed count). The sysadmin
// includes tiers per project; a prepaid tier takes a seat count (floating capacity).
// esco is metered — include-only, no seats.
export function TierSeatsEditor({
  type,
  catalogue,
  tiers,
  onChange,
}: {
  type: 'esco' | 'embedded' | 'demo';
  catalogue: readonly LicenseTier[];
  tiers: TierDraft[];
  onChange: (next: TierDraft[]) => void;
}) {
  const { t } = useTranslation('platform-projects');
  const metered = type === 'esco';
  const set = (code: string, patch: Partial<TierDraft>) =>
    onChange(tiers.map((row) => (row.code === code ? { ...row, ...patch } : row)));
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-[var(--color-text-secondary)]">
        {metered ? t('tierEditor.meteredNote') : t('tierEditor.prepaidNote')}
      </p>
      <ul className="flex flex-col gap-2">
        {catalogue.map((cat) => {
          const row = tiers.find((r) => r.code === cat.code);
          if (!row) return null;
          const cap =
            cat.max_active_power_systems == null
              ? t('tierEditor.unlimited')
              : t('tierEditor.cap', { value: formatNumber(cat.max_active_power_systems) });
          return (
            <li key={cat.code} className="flex items-center gap-3 rounded-md border border-[var(--color-border)] px-3 py-2">
              <Checkbox checked={row.included} onChange={(included) => set(cat.code, { included })} label={cat.name} />
              <span className="text-xs text-[var(--color-text-secondary)]">{cap}</span>
              <div className="ms-auto">
                {row.included && !metered && (
                  <div className="w-28">
                    <NumberInput
                      aria-label={t('tierEditor.seatsAria', { tier: cat.name })}
                      value={row.seats}
                      onChange={(seats) => set(cat.code, { seats: seats ?? 0 })}
                      min={0}
                      placeholder="0"
                    />
                  </div>
                )}
                {row.included && metered && <span className="text-xs text-[var(--color-text-disabled)]">{t('tierEditor.metered')}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── add-on toggles ────────────────────────────────────────────────────────────
// The three optional modules. On edit, `original` drives a warning when a module is
// turned OFF (it hides features + disables the related permissions for every member).
export function AddonToggles({
  value,
  original,
  onChange,
}: {
  value: Modules;
  original?: Modules;
  onChange: (next: Modules) => void;
}) {
  const { t } = useTranslation('platform-projects');
  const turningOff =
    original &&
    ((original.has_invoicing && !value.has_invoicing) ||
      (original.has_field_force && !value.has_field_force) ||
      (original.has_vehicles && !value.has_vehicles));
  return (
    <div className="flex flex-col gap-3">
      <Toggle checked={value.has_invoicing} onChange={(v) => onChange({ ...value, has_invoicing: v })} label={t('addons.invoicing')} />
      <Toggle checked={value.has_field_force} onChange={(v) => onChange({ ...value, has_field_force: v })} label={t('addons.fieldForce')} />
      <Toggle checked={value.has_vehicles} onChange={(v) => onChange({ ...value, has_vehicles: v })} label={t('addons.vehicles')} />
      {turningOff && <Alert variant="warning">{t('addons.turnOffWarning')}</Alert>}
    </div>
  );
}
